import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface LossReason {
  id: string;
  account_id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useLossReasons() {
  const { user } = useAuth();
  const [lossReasons, setLossReasons] = useState<LossReason[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLossReasons = useCallback(async () => {
    if (!user?.account?.id || !supabase) return;

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('account_loss_reasons')
        .select('*')
        .eq('account_id', user.account.id)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;
      setLossReasons(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.account?.id]);

  const createLossReason = async (name: string) => {
    if (!user?.account?.id || !supabase) return null;

    try {
      const { data, error: createError } = await supabase
        .from('account_loss_reasons')
        .insert({
          account_id: user.account.id,
          name,
          is_active: true
        })
        .select()
        .single();

      if (createError) throw createError;

      setLossReasons(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const updateLossReason = async (id: string, updates: Partial<LossReason>) => {
    if (!supabase) return false;

    try {
      const { error: updateError } = await supabase
        .from('account_loss_reasons')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchLossReasons();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const deleteLossReason = async (id: string) => {
    if (!supabase) return false;

    try {
      const { error: updateError } = await supabase
        .from('account_loss_reasons')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;
      setLossReasons(prev => prev.filter(r => r.id !== id));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  useEffect(() => {
    fetchLossReasons();
  }, [fetchLossReasons]);

  return {
    lossReasons,
    loading,
    error,
    fetchLossReasons,
    createLossReason,
    updateLossReason,
    deleteLossReason
  };
}
